import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  makeStyles,
  Box,
  Checkbox,
  Button,
  Typography
} from '@material-ui/core';
import {
  Visibility,
  LocationOn,
  Image,
  CheckCircle,
  Warning,
  Schedule,
  CameraAlt,
  Opacity,
  GetApp,
  ExpandMore,
  ExpandLess,
  Delete,
  Close
} from '@material-ui/icons';
import { format } from 'date-fns';
import axios from 'axios';

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 650
  },
  tableContainer: {
    maxHeight: 600
  },
  statusChip: {
    fontWeight: 'bold'
  },
  deadChip: {
    backgroundColor: '#ffcdd2',
    color: '#b71c1c'
  },
  dyingChip: {
    backgroundColor: '#ffe0b2',
    color: '#e65100'
  },
  identifiedChip: {
    backgroundColor: '#e3f2fd',
    color: '#0d47a1'
  },
  removedChip: {
    backgroundColor: '#f3e5f5',
    color: '#4a148c'
  },
  completedChip: {
    backgroundColor: '#c8e6c9',
    color: '#1b5e20'
  },
  pendingChip: {
    backgroundColor: '#fff9c4',
    color: '#f57f17'
  },
  reviveChip: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 'bold'
  },
  removeChip: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    fontWeight: 'bold'
  },
  replaceChip: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    fontWeight: 'bold'
  },
  needsApprovalChip: {
    border: '1px dashed #f9a825',
    backgroundColor: '#fff8e1',
    color: '#f57f17',
    fontWeight: 'bold'
  },
  approvedChip: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 'bold'
  },
  headerCell: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.grey[100]
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(0.5)
  },
  quantityBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    borderRadius: '50%',
    width: 24,
    height: 24,
    fontSize: '0.75rem',
    fontWeight: 'bold'
  },
  groupHeaderRow: {
    backgroundColor: theme.palette.grey[200],
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.grey[300]
    }
  },
  groupHeaderCell: {
    fontWeight: 'bold',
    padding: theme.spacing(1, 2)
  },
  groupCount: {
    marginLeft: theme.spacing(1),
    fontSize: '0.8rem',
    color: theme.palette.text.secondary
  },
  bulkActionBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(0.5, 0.5, 0, 0)
  },
  bulkActionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  bulkActionLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  }
}));

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  { id: 'select', label: '', sortable: false },
  { id: 'plantId', label: 'Plant ID', sortable: true },
  { id: 'type', label: 'Type', sortable: true },
  { id: 'state', label: 'Status', sortable: true },
  { id: 'quantity', label: 'Qty', sortable: true },
  { id: 'crewRecommendation', label: 'Recommendation', sortable: true },
  { id: 'approvedAction', label: 'Approved Action', sortable: true },
  { id: 'clientName', label: 'Client', sortable: true },
  { id: 'crewName', label: 'Crew', sortable: true },
  { id: 'reportedByName', label: 'Reported By', sortable: true },
  { id: 'dateReported', label: 'Date Reported', sortable: true },
  { id: 'actions', label: 'Actions', sortable: false }
];

const TOTAL_COLUMNS = headCells.length;

export default function PlantTrackingTable({ plants, onPlantClick, onQuickApprove, onBulkApprove, onBulkDelete, loading, apiUrl }) {
  const classes = useStyles();
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('dateReported');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [exporting, setExporting] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const toggleGroup = (groupKey) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = plantsArray.map((n) => n._id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const getStatusChip = (type, state) => {
    let chipClass = '';
    let label = state;
    let icon = null;

    const normalizedState = state.toUpperCase();

    switch (normalizedState) {
      case 'IDENTIFIED':
        chipClass = classes.identifiedChip;
        icon = <Schedule fontSize="small" />;
        label = 'Identified';
        break;
      case 'IMAGE_CAPTURED':
        chipClass = classes.pendingChip;
        icon = <CameraAlt fontSize="small" />;
        label = 'Image Captured';
        break;
      case 'LOCATED':
        chipClass = classes.pendingChip;
        icon = <LocationOn fontSize="small" />;
        label = 'Located';
        break;
      case 'REMOVED':
        chipClass = classes.removedChip;
        icon = <CheckCircle fontSize="small" />;
        label = 'Removed';
        break;
      case 'IRRIGATION_CONFIRMED':
        chipClass = classes.pendingChip;
        icon = <Opacity fontSize="small" />;
        label = 'Irrigation Confirmed';
        break;
      case 'COMPLETED':
        chipClass = classes.completedChip;
        icon = <CheckCircle fontSize="small" />;
        label = 'Completed';
        break;
      default:
        chipClass = classes.pendingChip;
        icon = <Warning fontSize="small" />;
        label = state;
    }

    return (
      <Chip
        size="small"
        label={label}
        className={`${classes.statusChip} ${chipClass}`}
        icon={icon}
      />
    );
  };

  const getTypeChip = (type) => {
    const chipClass = type === 'dead' ? classes.deadChip : classes.dyingChip;
    return (
      <Chip
        size="small"
        label={type}
        className={`${classes.statusChip} ${chipClass}`}
      />
    );
  };

  const getRecommendationChip = (rec) => {
    if (!rec) return <Typography variant="body2" color="textSecondary">--</Typography>;
    const chipMap = {
      revive: classes.reviveChip,
      remove: classes.removeChip,
      replace: classes.replaceChip
    };
    return (
      <Chip
        size="small"
        label={rec.charAt(0).toUpperCase() + rec.slice(1)}
        className={chipMap[rec] || classes.pendingChip}
      />
    );
  };

  const getApprovedActionChip = (plant) => {
    if (plant.approvedAction) {
      const chipMap = {
        revive: classes.reviveChip,
        remove: classes.removeChip,
        replace: classes.replaceChip
      };
      return (
        <Chip
          size="small"
          label={`Approved: ${plant.approvedAction.charAt(0).toUpperCase() + plant.approvedAction.slice(1)}`}
          className={chipMap[plant.approvedAction] || classes.approvedChip}
        />
      );
    }
    if (plant.crewRecommendation) {
      return (
        <Chip
          size="small"
          label="Needs Approval"
          className={classes.needsApprovalChip}
          variant="outlined"
        />
      );
    }
    return <Typography variant="body2" color="textSecondary">--</Typography>;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return format(new Date(date), 'MM/dd/yyyy');
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await axios.get(`${apiUrl}/api/hmi/plant-tracking/export`, {
        responseType: 'blob',
        params: {}
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `plant_tracking_export_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error);
    } finally {
      setExporting(false);
    }
  };

  // Ensure plants is an array
  const plantsArray = Array.isArray(plants) ? plants : [];

  const processedPlants = plantsArray.map(plant => ({
    ...plant,
    plantId: plant.plantId || plant._id?.substring(0, 8),
    jobSiteName: plant.jobSite?.name || '-',
    jobSiteId: plant.jobSite?._id || 'unknown',
    jobSiteAddress: plant.jobSite?.address || '',
    clientName: plant.client?.displayName || '-',
    crewName: plant.crew?.name || '-',
    dateReported: plant.timestamps?.created || plant.createdAt
  }));

  // Sort: primary by jobSiteName for grouping, secondary by user-selected sort
  const sortedPlants = useMemo(() => {
    const userSorted = stableSort(processedPlants, getComparator(order, orderBy));
    // Group by jobsite: sort by jobSiteName first, preserve user sort within groups
    userSorted.sort((a, b) => {
      if (a.jobSiteName < b.jobSiteName) return -1;
      if (a.jobSiteName > b.jobSiteName) return 1;
      return 0;
    });
    return userSorted;
  }, [processedPlants, order, orderBy]);

  const paginatedPlants = sortedPlants.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Build grouped rows for rendering
  const renderGroupedRows = () => {
    const rows = [];
    let lastJobSiteId = null;

    paginatedPlants.forEach((plant) => {
      const currentJobSiteId = plant.jobSiteId;

      // Insert group header when jobsite changes
      if (currentJobSiteId !== lastJobSiteId) {
        lastJobSiteId = currentJobSiteId;
        const groupPlantIds = paginatedPlants.filter(p => p.jobSiteId === currentJobSiteId).map(p => p._id);
        const groupPlantCount = groupPlantIds.length;
        const isCollapsed = collapsedGroups[currentJobSiteId];
        const selectedInGroup = groupPlantIds.filter(id => selected.includes(id)).length;
        const allGroupSelected = selectedInGroup === groupPlantCount;
        const someGroupSelected = selectedInGroup > 0 && !allGroupSelected;

        rows.push(
          <TableRow
            key={`group-${currentJobSiteId}`}
            className={classes.groupHeaderRow}
          >
            <TableCell padding="checkbox" className={classes.groupHeaderCell}>
              <Checkbox
                checked={allGroupSelected}
                indeterminate={someGroupSelected}
                onClick={(e) => {
                  e.stopPropagation();
                  if (allGroupSelected) {
                    // Deselect all in this group
                    setSelected(prev => prev.filter(id => !groupPlantIds.includes(id)));
                  } else {
                    // Select all in this group
                    setSelected(prev => [...new Set([...prev, ...groupPlantIds])]);
                  }
                }}
                style={{ color: 'inherit' }}
              />
            </TableCell>
            <TableCell
              colSpan={TOTAL_COLUMNS - 1}
              className={classes.groupHeaderCell}
              onClick={() => toggleGroup(currentJobSiteId)}
              style={{ cursor: 'pointer' }}
            >
              <Box display="flex" alignItems="center">
                {isCollapsed ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
                <Box ml={1}>
                  <Typography variant="subtitle2" component="span" style={{ fontWeight: 'bold' }}>
                    {plant.jobSiteName}
                  </Typography>
                  {plant.jobSiteAddress && (
                    <Typography variant="caption" component="span" style={{ marginLeft: 8, color: '#666' }}>
                      {plant.jobSiteAddress}
                    </Typography>
                  )}
                  <Typography variant="caption" component="span" className={classes.groupCount}>
                    ({groupPlantCount} plant{groupPlantCount !== 1 ? 's' : ''})
                  </Typography>
                </Box>
              </Box>
            </TableCell>
          </TableRow>
        );
      }

      // Skip plant rows if group is collapsed
      if (collapsedGroups[currentJobSiteId]) return;

      const isItemSelected = isSelected(plant._id);

      rows.push(
        <TableRow
          hover
          key={plant._id}
          selected={isItemSelected}
          className={classes.clickableRow}
        >
          <TableCell padding="checkbox">
            <Checkbox
              checked={isItemSelected}
              onClick={(event) => {
                event.stopPropagation();
                handleClick(event, plant._id);
              }}
            />
          </TableCell>
          <TableCell onClick={() => onPlantClick(plant)}>
            {plant.plantId}
          </TableCell>
          <TableCell onClick={() => onPlantClick(plant)}>
            {getTypeChip(plant.type)}
          </TableCell>
          <TableCell onClick={() => onPlantClick(plant)}>
            {getStatusChip(plant.type, plant.state)}
          </TableCell>
          <TableCell align="center" onClick={() => onPlantClick(plant)}>
            <Box className={classes.quantityBadge}>
              {plant.quantity || 1}
            </Box>
          </TableCell>
          <TableCell onClick={() => onPlantClick(plant)}>
            {getRecommendationChip(plant.crewRecommendation)}
          </TableCell>
          <TableCell onClick={() => onPlantClick(plant)}>
            {getApprovedActionChip(plant)}
          </TableCell>
          <TableCell onClick={() => onPlantClick(plant)}>
            {plant.client?.displayName}
          </TableCell>
          <TableCell onClick={() => onPlantClick(plant)}>
            {plant.crewName}
          </TableCell>
          <TableCell onClick={() => onPlantClick(plant)}>
            {plant.reportedByName || '-'}
          </TableCell>
          <TableCell onClick={() => onPlantClick(plant)}>
            {formatDate(plant.dateReported)}
          </TableCell>
          <TableCell>
            <Box className={classes.actionButtons}>
              {plant.crewRecommendation && !plant.approvedAction && onQuickApprove && (
                <Tooltip title={`Approve as: ${plant.crewRecommendation}`}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onQuickApprove(plant._id, plant.crewRecommendation);
                    }}
                    style={{ color: '#4caf50' }}
                  >
                    <CheckCircle fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="View Details">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlantClick(plant);
                  }}
                >
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
              {plant.location && (
                <Tooltip title="View on Map">
                  <IconButton size="small">
                    <LocationOn fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {(plant.photos?.before || plant.photos?.after) && (
                <Tooltip title="View Photos">
                  <IconButton size="small">
                    <Image fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </TableCell>
        </TableRow>
      );
    });

    return rows;
  };

  return (
    <Paper>
      <Box display="flex" justifyContent="flex-end" p={2}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<GetApp />}
          onClick={handleExport}
          disabled={exporting || plantsArray.length === 0}
        >
          {exporting ? 'Exporting...' : 'Export to Excel'}
        </Button>
      </Box>
      {selected.length > 0 && (
        <Box className={classes.bulkActionBar}>
          <Box className={classes.bulkActionLeft}>
            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
              {selected.length} selected
            </Typography>
            <Box className={classes.bulkActionButtons}>
              {onBulkApprove && (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => { onBulkApprove(selected, 'revive'); setSelected([]); }}
                    style={{ backgroundColor: '#4caf50', color: 'white' }}
                  >
                    Approve Revive
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => { onBulkApprove(selected, 'remove'); setSelected([]); }}
                    style={{ backgroundColor: '#f44336', color: 'white' }}
                  >
                    Approve Remove
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => { onBulkApprove(selected, 'replace'); setSelected([]); }}
                    style={{ backgroundColor: '#1976d2', color: 'white' }}
                  >
                    Approve Replace
                  </Button>
                </>
              )}
              {onBulkDelete && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Delete />}
                  onClick={() => { onBulkDelete(selected); setSelected([]); }}
                  style={{ backgroundColor: '#b71c1c', color: 'white' }}
                >
                  Delete
                </Button>
              )}
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setSelected([])} style={{ color: 'white' }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      )}
      <TableContainer className={classes.tableContainer}>
        <Table className={classes.table} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" className={classes.headerCell}>
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < plantsArray.length}
                  checked={plantsArray.length > 0 && selected.length === plantsArray.length}
                  onChange={handleSelectAllClick}
                />
              </TableCell>
              {headCells.slice(1).map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.id === 'quantity' ? 'center' : 'left'}
                  sortDirection={orderBy === headCell.id ? order : false}
                  className={classes.headerCell}
                >
                  {headCell.sortable ? (
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={(event) => handleRequestSort(event, headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  ) : (
                    headCell.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedPlants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={TOTAL_COLUMNS} align="center">
                  <Box py={4}>
                    <Typography variant="body1" color="textSecondary">
                      No plant tracking data found
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              renderGroupedRows()
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={plantsArray.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </Paper>
  );
}
